import type { TripPosition } from '../api'

const EARTH_RADIUS_KILOMETERS = 6_371.0088

function radians(degrees: number): number {
  return degrees * Math.PI / 180
}

function segmentDistanceKilometers(from: TripPosition, to: TripPosition): number {
  const latitudeDelta = radians(to.latitude - from.latitude)
  const longitudeDelta = radians(to.longitude - from.longitude)
  const fromLatitude = radians(from.latitude)
  const toLatitude = radians(to.latitude)
  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2
  return 2 * EARTH_RADIUS_KILOMETERS * Math.asin(Math.sqrt(haversine))
}

function timestamp(value: string): number {
  return Date.parse(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
}

export interface TripPointMetrics {
  distanceKilometers: number
  speedKilometersPerHour?: number
}

export function tripPointMetricsAt(positions: TripPosition[], index: number): TripPointMetrics {
  if (positions.length === 0 || index < 0) return { distanceKilometers: 0 }
  const normalizedIndex = Math.min(index, positions.length - 1)
  const current = positions[normalizedIndex]!
  let calculatedDistance = 0
  for (let positionIndex = 1; positionIndex <= normalizedIndex; positionIndex += 1) {
    calculatedDistance += segmentDistanceKilometers(
      positions[positionIndex - 1]!,
      positions[positionIndex]!,
    )
  }

  const distanceKilometers = current.distanceKilometers !== undefined
    ? current.distanceKilometers
    : calculatedDistance
  if (current.speedMetersPerSecond !== undefined) {
    return {
      distanceKilometers,
      speedKilometersPerHour: Math.max(0, current.speedMetersPerSecond * 3.6),
    }
  }

  const previous = positions[normalizedIndex - 1]
  if (!previous) return { distanceKilometers }
  const elapsedSeconds = (timestamp(current.recordedAt) - timestamp(previous.recordedAt)) / 1_000
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0) return { distanceKilometers }
  return {
    distanceKilometers,
    speedKilometersPerHour: segmentDistanceKilometers(previous, current) / elapsedSeconds * 3_600,
  }
}
